import React, { useState, useEffect, useRef } from 'react'
import { Typography, Card, Table, Tag, Button, Space, Statistic, Row, Col, Modal, message, Spin, Form, TimePicker, Switch, Select, Progress, List } from 'antd'
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  HistoryOutlined,
  RocketOutlined,
  ScheduleOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import axios from 'axios'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { Option } = Select

const Pipelines = () => {
  const [pipelines, setPipelines] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [runningFullPipeline, setRunningFullPipeline] = useState(false)
  const [historyModalVisible, setHistoryModalVisible] = useState(false)
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false)
  const [progressModalVisible, setProgressModalVisible] = useState(false)
  const [selectedPipeline, setSelectedPipeline] = useState(null)
  const [pipelineHistory, setPipelineHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [scheduleConfig, setScheduleConfig] = useState({
    enabled: false,
    schedule_time: '02:00',
    timezone: 'America/New_York'
  })
  const [currentExecutionId, setCurrentExecutionId] = useState(null)
  const [progressData, setProgressData] = useState(null)
  const progressIntervalRef = useRef(null)

  useEffect(() => {
    fetchPipelines()
    fetchStats()
    fetchScheduleConfig()

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchPipelines()
      fetchStats()
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  const fetchPipelines = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/pipelines/', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setPipelines(response.data)
    } catch (error) {
      console.error('Failed to fetch pipelines:', error)
      message.error('Failed to load pipelines')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/pipelines/stats', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setStats(response.data)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  const handleTrigger = async (pipelineName) => {
    Modal.confirm({
      title: 'Trigger Pipeline',
      content: `Are you sure you want to manually trigger the "${pipelineName}" pipeline?`,
      onOk: async () => {
        try {
          const token = localStorage.getItem('token')
          await axios.post(`/pipelines/${pipelineName}/trigger`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
          message.success(`Pipeline "${pipelineName}" triggered successfully`)

          // Refresh pipelines after a short delay
          setTimeout(() => {
            fetchPipelines()
            fetchStats()
          }, 1000)
        } catch (error) {
          console.error('Failed to trigger pipeline:', error)
          message.error(`Failed to trigger pipeline: ${error.response?.data?.detail || error.message}`)
        }
      }
    })
  }

  const fetchProgress = async (executionId) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`/pipelines/execution/${executionId}/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setProgressData(response.data)

      // Stop polling if completed or failed
      if (response.data.status === 'success' || response.data.status === 'failed') {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current)
          progressIntervalRef.current = null
        }
        setRunningFullPipeline(false)

        // Refresh pipelines
        fetchPipelines()
        fetchStats()

        // Show completion message
        if (response.data.status === 'success') {
          message.success('Pipeline completed successfully!')
        } else {
          message.error('Pipeline failed. Check the logs for details.')
        }
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error)
    }
  }

  const handleRunDailyPipeline = () => {
    Modal.confirm({
      title: 'Run Daily Data Pipeline',
      content: (
        <div>
          <p>This will run all 20 daily pipeline scripts with async + parallel execution:</p>
          <ul style={{ marginTop: '12px', marginBottom: '12px' }}>
            <li>Ticker Filtering (2 scripts)</li>
            <li>Core Data (4 scripts)</li>
            <li>Technical Indicators (4 scripts)</li>
            <li>Corporate Actions (2 scripts)</li>
            <li>News & Events (2 scripts)</li>
            <li>Fundamentals (4 scripts)</li>
            <li>IPOs & Other (2 scripts)</li>
          </ul>
          <p>Estimated duration: <strong>20-25 minutes (async + parallel execution)</strong></p>
          <p style={{ color: '#cf1322', marginTop: '12px' }}>
            ⚠️ This will process 3,197 qualified stocks (small cap+)
          </p>
        </div>
      ),
      okText: 'Run Pipeline',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setRunningFullPipeline(true)
          const token = localStorage.getItem('token')

          const response = await axios.post('/pipelines/run-daily', {}, {
            headers: { Authorization: `Bearer ${token}` }
          })

          const executionId = response.data.execution_id
          setCurrentExecutionId(executionId)
          setProgressData(null)
          setProgressModalVisible(true)

          message.success('Daily pipeline execution started!')

          progressIntervalRef.current = setInterval(() => {
            fetchProgress(executionId)
          }, 2000)

          fetchProgress(executionId)
        } catch (error) {
          console.error('Failed to run daily pipeline:', error)
          message.error(`Failed to start pipeline: ${error.response?.data?.detail || error.message}`)
          setRunningFullPipeline(false)
        }
      }
    })
  }

  const handleRunWeeklyPipeline = () => {
    Modal.confirm({
      title: 'Run Weekly ML Training Pipeline',
      content: (
        <div>
          <p>This will train all ML models with the latest data:</p>
          <ul style={{ marginTop: '12px', marginBottom: '12px' }}>
            <li>Train ML models for all strategies</li>
            <li>Update model performance metrics</li>
            <li>Generate training reports</li>
          </ul>
          <p>Estimated duration: <strong>20-30 minutes</strong></p>
          <p style={{ color: '#1890ff', marginTop: '12px' }}>
            ℹ️ This trains machine learning models for portfolio optimization
          </p>
        </div>
      ),
      okText: 'Run Training',
      okButtonProps: { danger: false, type: 'primary' },
      onOk: async () => {
        try {
          setRunningFullPipeline(true)
          const token = localStorage.getItem('token')

          const response = await axios.post('/pipelines/run-weekly', {}, {
            headers: { Authorization: `Bearer ${token}` }
          })

          const executionId = response.data.execution_id
          setCurrentExecutionId(executionId)
          setProgressData(null)
          setProgressModalVisible(true)

          message.success('Weekly ML training started!')

          progressIntervalRef.current = setInterval(() => {
            fetchProgress(executionId)
          }, 2000)

          fetchProgress(executionId)
        } catch (error) {
          console.error('Failed to run weekly pipeline:', error)
          message.error(`Failed to start training: ${error.response?.data?.detail || error.message}`)
          setRunningFullPipeline(false)
        }
      }
    })
  }

  const handleRunMonthlyPipeline = () => {
    Modal.confirm({
      title: 'Run Monthly RL Training Pipeline',
      content: (
        <div>
          <p>This will train all RL models with the latest data:</p>
          <ul style={{ marginTop: '12px', marginBottom: '12px' }}>
            <li>Train reinforcement learning models</li>
            <li>Update RL policy networks</li>
            <li>Generate performance analytics</li>
          </ul>
          <p>Estimated duration: <strong>30-60 minutes</strong></p>
          <p style={{ color: '#722ed1', marginTop: '12px' }}>
            ℹ️ This trains deep RL models for advanced trading strategies
          </p>
        </div>
      ),
      okText: 'Run Training',
      okButtonProps: { danger: false, type: 'primary' },
      onOk: async () => {
        try {
          setRunningFullPipeline(true)
          const token = localStorage.getItem('token')

          const response = await axios.post('/pipelines/run-monthly', {}, {
            headers: { Authorization: `Bearer ${token}` }
          })

          const executionId = response.data.execution_id
          setCurrentExecutionId(executionId)
          setProgressData(null)
          setProgressModalVisible(true)

          message.success('Monthly RL training started!')

          progressIntervalRef.current = setInterval(() => {
            fetchProgress(executionId)
          }, 2000)

          fetchProgress(executionId)
        } catch (error) {
          console.error('Failed to run monthly pipeline:', error)
          message.error(`Failed to start training: ${error.response?.data?.detail || error.message}`)
          setRunningFullPipeline(false)
        }
      }
    })
  }

  const handleRunFullPipeline = () => {
    Modal.confirm({
      title: 'Run Full Daily Data Pipeline',
      content: (
        <div>
          <p>This will run all 20 pipeline scripts with async + parallel execution:</p>
          <ul style={{ marginTop: '12px', marginBottom: '12px' }}>
            <li>Ticker Filtering (2 scripts) - Updates list & qualifications</li>
            <li>Core Data (4 scripts)</li>
            <li>Technical Indicators (4 scripts)</li>
            <li>Corporate Actions (3 scripts)</li>
            <li>News & Events (2 scripts)</li>
            <li>Fundamentals (4 scripts)</li>
            <li>IPOs & Other (1 script)</li>
          </ul>
          <p>Estimated duration: <strong>20-25 minutes (async + parallel execution)</strong></p>
          <p style={{ color: '#cf1322', marginTop: '12px' }}>
            ⚠️ This will process 3,197 qualified stocks (small cap+) - 3-4x faster than before!
          </p>
        </div>
      ),
      okText: 'Run Pipeline',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setRunningFullPipeline(true)
          const token = localStorage.getItem('token')

          const response = await axios.post('/pipelines/run-full', {}, {
            headers: { Authorization: `Bearer ${token}` }
          })

          const executionId = response.data.execution_id
          setCurrentExecutionId(executionId)
          setProgressData(null)
          setProgressModalVisible(true)

          message.success('Full pipeline execution started!')

          // Start polling for progress
          progressIntervalRef.current = setInterval(() => {
            fetchProgress(executionId)
          }, 2000)

          // Initial fetch
          fetchProgress(executionId)
        } catch (error) {
          console.error('Failed to run full pipeline:', error)
          message.error(`Failed to start pipeline: ${error.response?.data?.detail || error.message}`)
          setRunningFullPipeline(false)
        }
      }
    })
  }

  useEffect(() => {
    // Cleanup interval on unmount
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
    }
  }, [])

  const fetchScheduleConfig = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/pipelines/schedule', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setScheduleConfig(response.data)
    } catch (error) {
      console.error('Failed to fetch schedule config:', error)
    }
  }

  const handleScheduleSubmit = async (values) => {
    try {
      const token = localStorage.getItem('token')
      await axios.put('/pipelines/schedule', values, {
        headers: { Authorization: `Bearer ${token}` }
      })
      message.success('Pipeline schedule updated successfully')
      setScheduleModalVisible(false)
      fetchScheduleConfig()
    } catch (error) {
      console.error('Failed to update schedule:', error)
      message.error('Failed to update pipeline schedule')
    }
  }

  const handleViewHistory = async (pipeline) => {
    setSelectedPipeline(pipeline)
    setHistoryModalVisible(true)
    setHistoryLoading(true)

    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`/pipelines/${pipeline.name}/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setPipelineHistory(response.data)
    } catch (error) {
      console.error('Failed to fetch history:', error)
      message.error('Failed to load pipeline history')
    } finally {
      setHistoryLoading(false)
    }
  }

  const getStatusTag = (status) => {
    const statusConfig = {
      success: { color: 'success', icon: <CheckCircleOutlined /> },
      failed: { color: 'error', icon: <CloseCircleOutlined /> },
      running: { color: 'processing', icon: <SyncOutlined spin /> },
      idle: { color: 'default', icon: <ClockCircleOutlined /> }
    }

    const config = statusConfig[status] || statusConfig.idle

    return (
      <Tag color={config.color} icon={config.icon}>
        {status.toUpperCase()}
      </Tag>
    )
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '-'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
  }

  // Define execution order based on run_daily_data_pipeline.sh
  const executionOrder = [
    // Step 1: Core Data Updates
    'update_ticker_overview',
    'update_ticker_universe',
    'update_daily_bars',
    'update_etf_bars',
    // Step 2: Technical Indicators
    'update_sma',
    'update_ema',
    'update_rsi',
    'update_macd',
    // Step 3: Corporate Actions
    'update_splits',
    'update_dividends',
    'update_short_interest',
    // Step 4: News & Events
    'update_news',
    'update_ticker_events',
    // Step 5: Fundamentals
    'update_balance_sheets',
    'update_income_statements',
    'update_cash_flow_statements',
    'update_ratios',
    // Step 6: IPOs & Other
    'update_ipos'
  ]

  const stepLabels = {
    'update_ticker_overview': 'Step 1: Core Data',
    'update_ticker_universe': 'Step 1: Core Data',
    'update_daily_bars': 'Step 1: Core Data',
    'update_etf_bars': 'Step 1: Core Data',
    'update_sma': 'Step 2: Technical Indicators',
    'update_ema': 'Step 2: Technical Indicators',
    'update_rsi': 'Step 2: Technical Indicators',
    'update_macd': 'Step 2: Technical Indicators',
    'update_splits': 'Step 3: Corporate Actions',
    'update_dividends': 'Step 3: Corporate Actions',
    'update_short_interest': 'Step 3: Corporate Actions',
    'update_news': 'Step 4: News & Events',
    'update_ticker_events': 'Step 4: News & Events',
    'update_balance_sheets': 'Step 5: Fundamentals',
    'update_income_statements': 'Step 5: Fundamentals',
    'update_cash_flow_statements': 'Step 5: Fundamentals',
    'update_ratios': 'Step 5: Fundamentals',
    'update_ipos': 'Step 6: IPOs & Other'
  }

  // Sort pipelines by execution order
  const sortedPipelines = [...pipelines].sort((a, b) => {
    const aIndex = executionOrder.indexOf(a.name)
    const bIndex = executionOrder.indexOf(b.name)
    if (aIndex === -1 && bIndex === -1) return 0
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })

  const columns = [
    {
      title: 'Order',
      key: 'order',
      render: (_, record) => {
        const index = executionOrder.indexOf(record.name)
        return index !== -1 ? (
          <Tag color="blue">{index + 1}</Tag>
        ) : (
          <Tag color="default">-</Tag>
        )
      },
      width: '7%'
    },
    {
      title: 'Pipeline Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.description}</Text>
          {stepLabels[name] && (
            <Tag color="cyan" style={{ fontSize: '10px', marginTop: '4px' }}>
              {stepLabels[name]}
            </Tag>
          )}
        </Space>
      ),
      width: '23%'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      width: '12%'
    },
    {
      title: 'Last Run',
      key: 'last_run',
      render: (_, record) => {
        if (!record.last_run) return <Text type="secondary">Never</Text>
        const date = new Date(record.last_run.started_at)
        return (
          <Space direction="vertical" size={0}>
            <Text>{date.toLocaleDateString()}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>{date.toLocaleTimeString()}</Text>
          </Space>
        )
      },
      width: '15%'
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => {
        if (!record.last_run?.duration_seconds) return <Text type="secondary">-</Text>
        return formatDuration(record.last_run.duration_seconds)
      },
      width: '10%'
    },
    {
      title: 'Success Rate (7d)',
      dataIndex: 'success_rate_7d',
      key: 'success_rate_7d',
      render: (rate) => {
        const color = rate >= 90 ? 'success' : rate >= 70 ? 'warning' : 'error'
        return <Tag color={color}>{rate.toFixed(1)}%</Tag>
      },
      width: '13%'
    },
    {
      title: 'Avg Duration',
      dataIndex: 'avg_duration_seconds',
      key: 'avg_duration_seconds',
      render: (duration) => duration ? formatDuration(Math.round(duration)) : '-',
      width: '10%'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => handleTrigger(record.name)}
            disabled={record.status === 'running' || runningFullPipeline}
          >
            Trigger
          </Button>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => handleViewHistory(record)}
          >
            History
          </Button>
        </Space>
      ),
      width: '15%'
    }
  ]

  const historyColumns = [
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (date) => new Date(date).toLocaleString(),
      width: '30%'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      width: '15%'
    },
    {
      title: 'Duration',
      dataIndex: 'duration_seconds',
      key: 'duration_seconds',
      render: (duration) => formatDuration(duration),
      width: '15%'
    },
    {
      title: 'Records',
      key: 'records',
      render: (_, record) => `${record.records_processed || 0} / ${record.records_failed || 0} failed`,
      width: '20%'
    },
    {
      title: 'Triggered By',
      dataIndex: 'triggered_by',
      key: 'triggered_by',
      render: (triggeredBy) => triggeredBy || 'Automated',
      width: '20%'
    }
  ]

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <Title level={2} style={{ marginBottom: '4px' }}>Data Pipeline Management</Title>
          <Text type="secondary">Monitor and control daily data pipeline executions</Text>
        </div>
        <Space size="middle" wrap>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={handleRunDailyPipeline}
            loading={runningFullPipeline}
            size="large"
          >
            Run Daily Pipeline
          </Button>
        </Space>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <Row gutter={16} style={{ marginTop: '24px', marginBottom: '24px' }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Executions Today"
                value={stats.total_executions_today}
                prefix={<SyncOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Successful Today"
                value={stats.successful_executions_today}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Success Rate Today"
                value={stats.success_rate_today}
                precision={1}
                suffix="%"
                valueStyle={{ color: stats.success_rate_today >= 90 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="7-Day Success Rate"
                value={stats.success_rate_7d}
                precision={1}
                suffix="%"
                valueStyle={{ color: stats.success_rate_7d >= 90 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Pipelines Table */}
      <Card
        title={<Space><SyncOutlined />Daily Data Pipeline Scripts (Execution Order)</Space>}
        extra={
          <Button
            icon={<SyncOutlined />}
            onClick={() => {
              fetchPipelines()
              fetchStats()
            }}
          >
            Refresh
          </Button>
        }
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Scripts run in the order shown below. The daily pipeline processes 3,197 qualified stocks (small cap+ US common stock) - 73% reduction from 11,953 total tickers!
          </Text>
        </div>
        <Table
          columns={columns}
          dataSource={sortedPipelines}
          rowKey="name"
          loading={loading}
          pagination={false}
          size="middle"
        />
      </Card>

      {/* History Modal */}
      <Modal
        title={selectedPipeline ? `Execution History: ${selectedPipeline.name}` : 'Execution History'}
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={null}
        width={900}
      >
        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={historyColumns}
            dataSource={pipelineHistory}
            rowKey="execution_id"
            pagination={{ pageSize: 10 }}
            size="small"
          />
        )}
      </Modal>

      {/* Progress Modal */}
      <Modal
        title={
          <Space>
            <RocketOutlined />
            Full Pipeline Execution Progress
          </Space>
        }
        open={progressModalVisible}
        onCancel={() => {
          setProgressModalVisible(false)
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current)
            progressIntervalRef.current = null
          }
        }}
        footer={null}
        width={700}
        closable={progressData?.status === 'success' || progressData?.status === 'failed'}
        maskClosable={false}
      >
        {progressData ? (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <Progress
                percent={
                  progressData.progress
                    ? Math.round((progressData.progress.current_step / progressData.progress.total_steps) * 100)
                    : 0
                }
                status={
                  progressData.status === 'running' ? 'active' :
                  progressData.status === 'success' ? 'success' :
                  progressData.status === 'failed' ? 'exception' : 'normal'
                }
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <div style={{ marginTop: '8px', textAlign: 'center' }}>
                <Text strong>
                  {progressData.progress ? (
                    <>
                      Step {progressData.progress.current_step} of {progressData.progress.total_steps}
                      {progressData.status === 'running' && ' - ' + progressData.progress.description}
                    </>
                  ) : (
                    'Initializing...'
                  )}
                </Text>
              </div>
            </div>

            {progressData.progress && (
              <div style={{ marginBottom: '16px' }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text type="secondary">Current Script:</Text>
                  <Text strong>{progressData.progress.current_script || 'Starting...'}</Text>
                </Space>
              </div>
            )}

            {progressData.progress?.failed_scripts && progressData.progress.failed_scripts.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <Text type="danger" strong>Failed Scripts:</Text>
                <List
                  size="small"
                  dataSource={progressData.progress.failed_scripts}
                  renderItem={item => (
                    <List.Item>
                      <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        <Text type="danger">{item.script}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {item.error.substring(0, 200)}...
                        </Text>
                      </Space>
                    </List.Item>
                  )}
                  style={{ marginTop: '8px' }}
                />
              </div>
            )}

            <div style={{ marginTop: '24px', padding: '12px', background: '#f0f5ff', borderRadius: '4px' }}>
              <Space direction="vertical" size={4}>
                <Space>
                  <Text type="secondary">Status:</Text>
                  <Tag color={
                    progressData.status === 'running' ? 'processing' :
                    progressData.status === 'success' ? 'success' :
                    'error'
                  }>
                    {progressData.status.toUpperCase()}
                  </Tag>
                </Space>
                <Space>
                  <Text type="secondary">Started:</Text>
                  <Text>{new Date(progressData.started_at).toLocaleString()}</Text>
                </Space>
                {progressData.completed_at && (
                  <Space>
                    <Text type="secondary">Completed:</Text>
                    <Text>{new Date(progressData.completed_at).toLocaleString()}</Text>
                  </Space>
                )}
                {progressData.duration_seconds && (
                  <Space>
                    <Text type="secondary">Duration:</Text>
                    <Text>{Math.floor(progressData.duration_seconds / 60)}m {progressData.duration_seconds % 60}s</Text>
                  </Space>
                )}
              </Space>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" tip="Starting pipeline execution..." />
          </div>
        )}
      </Modal>

      {/* Schedule Configuration Modal */}
      <Modal
        title={
          <Space>
            <ScheduleOutlined />
            Configure Pipeline Schedule
          </Space>
        }
        open={scheduleModalVisible}
        onCancel={() => setScheduleModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          layout="vertical"
          initialValues={{
            enabled: scheduleConfig.enabled,
            schedule_time: scheduleConfig.schedule_time ? dayjs(scheduleConfig.schedule_time, 'HH:mm') : dayjs('02:00', 'HH:mm'),
            timezone: scheduleConfig.timezone || 'America/New_York'
          }}
          onFinish={(values) => {
            handleScheduleSubmit({
              enabled: values.enabled,
              schedule_time: values.schedule_time.format('HH:mm'),
              timezone: values.timezone
            })
          }}
        >
          <Form.Item
            name="enabled"
            label="Enable Automatic Scheduling"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="schedule_time"
            label="Scheduled Time"
            help="Time when the pipeline should run daily"
          >
            <TimePicker
              format="HH:mm"
              style={{ width: '100%' }}
              showNow={false}
            />
          </Form.Item>

          <Form.Item
            name="timezone"
            label="Timezone"
          >
            <Select>
              <Option value="America/New_York">Eastern Time (ET)</Option>
              <Option value="America/Chicago">Central Time (CT)</Option>
              <Option value="America/Denver">Mountain Time (MT)</Option>
              <Option value="America/Los_Angeles">Pacific Time (PT)</Option>
              <Option value="UTC">UTC</Option>
            </Select>
          </Form.Item>

          <div style={{ background: '#f0f5ff', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <strong>Note:</strong> The pipeline runs after market close to ensure all daily data is available.
              The recommended schedule is 2:00 AM ET (after markets close at 4:00 PM ET + 10 hours for data availability).
            </Text>
          </div>

          <div style={{ background: '#fffbe6', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
            <Text type="warning" style={{ fontSize: '12px' }}>
              <strong>Current Schedule:</strong> {scheduleConfig.enabled ? (
                <>
                  Enabled - Runs daily at <strong>{scheduleConfig.schedule_time}</strong> {scheduleConfig.timezone}
                </>
              ) : (
                'Disabled - Pipeline must be run manually'
              )}
            </Text>
          </div>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Save Schedule
              </Button>
              <Button onClick={() => setScheduleModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Pipelines
